import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { API_PATIENT_CALLS } from 'services/dataService/ccmResources';
import { showResult } from 'infrastructure/hooks/utils';
import { DateFormatter, isRpmModule } from 'infrastructure/functions';
import { DateFormats, ModuleType } from 'infrastructure/enums';

import type { SortState, DateRange } from 'infrastructure/types';
import type {
  IPartialPaginationMeta,
  IVoiceCallResponse,
} from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
  meta: IPartialPaginationMeta;
  module: ModuleType;
  documentationTimeSort?: SortState;
  dateRange?: DateRange | [];
}

type TFilter = {
  patientGuid: string;
  startTime?: string;
  endTime?: string;
  module: ModuleType;
  documentationTimeSort?: string;
} & IPartialPaginationMeta;

export const getVoiceCallsFn = async ({ module, ...filter }: TFilter) => {
  let url = API_PATIENT_CALLS;

  if (isRpmModule(module)) url = `core/patients/${filter.patientGuid}/calls`;

  const { data, error } = await dataService.getList<IVoiceCallResponse>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getVoiceCallsKey = (filter: TFilter) => ['getVoiceCalls', filter];

export const VOICE_CALLS_PER_PAGE = 5;

export const useGetVoiceCalls = ({
  patientGuid,
  meta,
  module = ModuleType.CCM,
  documentationTimeSort,
  dateRange = [],
}: Props) => {
  const [startTime, endTime] = dateRange;

  const filter: TFilter = {
    ...meta,
    patientGuid,
    items: VOICE_CALLS_PER_PAGE,
    module,
    startTime: DateFormatter({
      date: startTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    endTime: DateFormatter({
      date: endTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    documentationTimeSort: documentationTimeSort?.toUpperCase(),
  };

  const {
    data: calls,
    isLoading,
    isFetching,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: getVoiceCallsKey(filter),
    queryFn: async () => getVoiceCallsFn({ ...filter }),
    enabled: Boolean(startTime) && Boolean(endTime),
  });

  return {
    calls,
    isLoading: isLoading || isFetching || isRefetching,
    isFetching,
    ...other,
  };
};
